import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "../../assets/images/logo.png";
import avtar from "../../assets/images/user.png";
import { useDispatch, useSelector } from "react-redux";
import { fynitoApi } from "../Services/axios";
import { logoutUser } from "../../redux/features/auth/authSliceNew";
import Swal from "sweetalert2";
import SearchBar from "./SearchBar";
import "./header.css";
import {
  setKey,
  setLanguage,
  setLocationType,
  fromLatLng,
} from "react-geocode";
import toastify from "../../helper/toastify";
import LocationSearch from "../Home/LocationSearch";
import { fetchAddress } from "../../redux/features/address/addressSlice";
import {
  setIsLoadingLocation,
  setLocation,
} from "../../redux/features/location/locationSlice";
import { loadingStart, loadingStop } from "../../redux/features/loading/loadingSlice";
import { DEFAULT_APP_DOWNLOAD_URL } from "../../config/appDownload";
function Header() {
  const { user, isLogin } = useSelector((state) => state.auth);
  const [showDropdown, setShowDropdown] = useState(false);
  const cartItem = useSelector((state) => state.cart);
  const totalQuantity = cartItem?.cart?.reduce(
    (total, item) => total + item.quantity,
    0
  );
  const dispatch = useDispatch();
  const [activeDiv, setActiveDiv] = useState(false);
  const toggleLocationDropdown = () => setActiveDiv(!activeDiv);
  const navigate = useNavigate();
  useEffect(() => {
    setKey("AIzaSyDwOFrgNmM51uT1F1zg_L0Em5ujW9obcHk");
    setLanguage("en");
    setLocationType("ROOFTOP");
  }, []);

  useEffect(() => {
    // Check if we're waiting for location after page reload
    const waitingForLocation = localStorage.getItem("waitingForLocation");
    const storedLocation = localStorage.getItem("userLocation");
    
    if (waitingForLocation === "true") {
      // If we have a stored location with address, we're done
      if (storedLocation) {
        try {
          const parsedLocation = JSON.parse(storedLocation);
          if (parsedLocation.address && parsedLocation.latitude && parsedLocation.longitude) {
            // Location is set, clear the flag and stop loading
            localStorage.removeItem("waitingForLocation");
            dispatch(loadingStop());
            dispatch(setIsLoadingLocation(false));
            return;
          }
        } catch (e) {
          // Invalid stored location, continue waiting
        }
      }
      
      // Still waiting for location, keep loader showing
      dispatch(loadingStart());
      dispatch(setIsLoadingLocation(true));
    } else if (!storedLocation) {
      // Only request current location if no location is saved and not waiting
      requestLocationPermission();
    }
  }, [dispatch]);

  // Handle Logout
  const handleLogout = async () => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will be logged out from your account.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, logout!",
    });

    if (result.isConfirmed) {
      try {
        await fynitoApi.post("/logout");
              localStorage.removeItem("userLocation");
              window.location.reload();
        dispatch(logoutUser());
        Swal.fire("Logged Out", "You have been logged out.", "success");
      } catch (error) {
        Swal.fire("Error", "Logout failed. Please try again.", "error");
      }
    }
  };
  const { addressList } = useSelector((state) => state.address);
  const { location, isLoading } = useSelector((state) => state.location);

  // Update location and persist to localStorage
  const updateLocation = (newLocation) => {
    if (!newLocation) return;

    // Ensure we have a valid location object
    const locationToSave = {
      latitude: newLocation.latitude || null,
      longitude: newLocation.longitude || null,
      address: newLocation.address || "",
    };

    // Update Redux state (this will trigger LocationSearch component to update)
    dispatch(setLocation(locationToSave));
    localStorage.setItem("userLocation", JSON.stringify(locationToSave));

    // If we have coordinates and user is authenticated, update the current location on the server
    const token = localStorage.getItem('token');
    if (locationToSave.latitude && locationToSave.longitude && token) {
      updateCurrentLocation(locationToSave.latitude, locationToSave.longitude).catch(() => {
        // Silently handle errors - location is still saved locally
      });
    }
    
    // Check if we were waiting for location
    const waitingForLocation = localStorage.getItem("waitingForLocation");
    if (waitingForLocation === "true" && locationToSave.address) {
      // Location is now set, wait a bit for UI to update, then clear flag and reload
      setTimeout(() => {
        localStorage.removeItem("waitingForLocation");
        dispatch(loadingStop());
        dispatch(setIsLoadingLocation(false));
        // Reload after location is confirmed to be set
        window.location.reload();
      }, 500); // Give time for address to appear in search bar
    }
    
    setActiveDiv(false);
    navigate("/");
  };

  const requestLocationPermission = (showLoader = false) => {
    if (!navigator.geolocation) {
      return toastify.error("Geolocation is not supported by your browser");
    }
    
    dispatch(setIsLoadingLocation(true));
    if (showLoader) {
      dispatch(loadingStart());
    }
    
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const { latitude, longitude, accuracy } = coords;
        
        try {
          const address = await getAddressFromCoords(latitude, longitude);
          // Update location state and display in search field
          // updateLocation will handle the loader and reload if needed
          updateLocation({ latitude, longitude, address });
          setActiveDiv(false);
          navigate("/");
        } catch (error) {
          toastify.error("Failed to get address from coordinates");
          // Clear waiting flag on error
          localStorage.removeItem("waitingForLocation");
          dispatch(loadingStop());
          dispatch(setIsLoadingLocation(false));
        }
      },
      (error) => {
        // Clear waiting flag on error
        localStorage.removeItem("waitingForLocation");
        dispatch(loadingStop());
        dispatch(setIsLoadingLocation(false));
        const messages = {
          1: "Please enable location permissions in your browser settings.",
          2: "Location information is unavailable.",
          3: "The request to get location timed out.",
        };
        toastify.error(messages[error.code] || "Location access denied.");
      },
      { enableHighAccuracy: true, timeout: 30000, maximumAge: 0 }
    );
  };

  // Function to get fresh current location (clears saved location first)
  const getFreshCurrentLocation = () => {
    // Set flag to indicate we're waiting for location
    localStorage.setItem("waitingForLocation", "true");
    
    // Clear saved location to force fresh location request
    localStorage.removeItem("userLocation");
    dispatch(setLocation({ latitude: null, longitude: null, address: '' }));
    
    // Start loader and request location
    requestLocationPermission(true);
  };
  const getAddressFromCoords = async (lat, lng) => {
    try {
      const response = await fromLatLng(lat, lng);
      const address = response?.results[0]?.formatted_address;
      return address || "Unknown Location";
    } catch (error) {
      return "Unknown Location";
    }
  };

  const updateCurrentLocation = async (lat, lng, id = null) => {
    // Only update location if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      // Silently return if user is not authenticated - no need to update location on server
      return;
    }
    
    try {
      const payload = {
        user_current_lat: lat.toFixed(4),
        user_current_long: lng.toFixed(4),
        id: id ? id.toString() : "",
        status: id ? "1" : "",
        compair_lat: lat.toFixed(2),
        compair_long: lng.toFixed(2),
      };
      const res = await fynitoApi.post("/current-location-update", payload);
      return res;
    } catch (error) {
      // Check if this is a 401 error that's already been handled by the interceptor
      const is401Error = error.response?.status === 401 || 
                        error.status === 401 ||
                        error._isHandled401 ||
                        error.isHandled;
      
      // If it's a handled 401, silently return (interceptor already handled logout/redirect)
      if (is401Error) {
        return; // Silently handle 401 errors
      }
      
      // Only log non-401 errors
      console.error("Error updating current location:", error);
      throw error;
    }
  };

  const handleSavedAddressSelect = async (addressObj) => {
    const { latitude, longitude, address, id } = addressObj;
    const updatedLocation = {
      latitude,
      longitude,
      address,
    };

    // Update location state first
    updateLocation(updatedLocation);
    localStorage.setItem("userLocation", JSON.stringify(updatedLocation));
    setActiveDiv(false);

    // Wait for the location update API call (only if authenticated)
    try {
      await updateCurrentLocation(latitude, longitude, id);
    } catch (error) {
      // Silently handle errors - location is still saved locally
    }

    // Explicitly fetch home data with the new location
    navigate("/");
    
    // Auto-reload the page after a brief delay to ensure all data is fetched with new location
    setTimeout(() => {
      window.location.reload();
    }, 250); // 250ms delay to allow data fetching to complete
  };

  // Fetch address only once on mount, not on every render
  // Note: fetchAddress now has built-in caching and deduplication, so multiple calls are safe
  useEffect(() => {
    // Only fetch if address list is empty (prevents unnecessary calls)
    if (!addressList || addressList.length === 0) {
      dispatch(fetchAddress());
    }
  }, [dispatch, addressList]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.querySelector(".mobile-profile-dropdown");
      if (dropdown && !dropdown.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <header className="theme-header fixed-top">
        <nav className="navbar navbar-expand-lg">
          <div className="container header-body col-12">
            <div className="row">
              <div className="col-2 d-flex align-items-center justify-content-center">
                <Link className="navbar-brand" to="/">
                  <img src={logo} alt="logo" />
                </Link>
              </div>

              <div className="col-8 col-lg-10">
                <div className="header-new-filter">
                  <div className="header-new-filter-location">
                    <LocationSearch
                      onLocationSelect={updateLocation}
                      currentAddress={location.address}
                    />
                    <span className="fas fa-map-marker-alt new-icon"></span>
                    <button
                      className={`drop-btn ${activeDiv ? "round" : ""}`}
                      type="button"
                      onClick={toggleLocationDropdown}
                    >
                      <i className="fas fa-chevron-down"></i>
                    </button>
                    {activeDiv && (
                      <div className="location-filter">
                        <button
                          className="current-loc"
                          type="button"
                          onClick={getFreshCurrentLocation}
                          disabled={isLoading}
                        >
                          <i className="fas fa-map-marker-alt me-1"></i>
                          {isLoading
                            ? "Getting location..."
                            : "Use my current location"}
                        </button>
                        <h5>Recent searches</h5>
                        <ul className="location-filter-list">
                          {location.address && (
                            <li>
                              <Link to="" onClick={() => setActiveDiv(false)}>
                                <i className="fas fa-map-marker-alt me-1"></i>
                                {location.address}
                              </Link>
                            </li>
                          )}
                        </ul>
                        <h5>
                          <strong> Save Addresses</strong>
                        </h5>
                        <ul className="location-filter-list">
                          {addressList?.length > 0 ? (
                            addressList?.map((add, index) => {
                              // console.log(add)
                              return (
                                <li key={add.id}>
                                  <Link
                                    to=""
                                    onClick={() =>
                                      handleSavedAddressSelect({
                                        latitude: parseFloat(add.lat),
                                        longitude: parseFloat(add.long),
                                        address: add.address,
                                        id: add.id,
                                      })
                                    }
                                  >
                                    <i className="fas fa-map-marker-alt me-1"></i>
                                    {add.address}
                                  </Link>
                                </li>
                              );
                            })
                          ) : (
                            <p>No Saved Address Found. Please Add Address</p>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-2 d-flex align-items-center justify-content-center d-lg-none">
                <button
                  className="btn user-icon-btn"
                  onClick={() => {
                    if (!isLogin) {
                      const loginBtn = document.getElementById("loginbutton");
                      loginBtn?.click();
                    } else {
                      setShowDropdown(!showDropdown); // use a state hook for toggling
                    }
                  }}
                >
                  <i className="fas fa-user"></i>
                </button>
              </div>
            </div>

            {/* mobile profile dropdown */}

            {isLogin && showDropdown && window.innerWidth <= 991 && (
              <div className="mobile-profile-dropdown">
                <div className="d-flex align-items-center justify-content-between px-3 py-2">
                  <div>
                    <img
                      src={user?.profile || avtar}
                      alt="profile"
                      className="rounded-circle prof-img me-2"
                      width="32"
                      height="32"
                    />
                    <span>{user?.name || "Profile"}</span>
                  </div>
                  <i
                    className={`fas ${
                      showDropdown ? "fa-chevron-up" : "fa-chevron-down"
                    }`}
                    style={{ cursor: "pointer", paddingRight: "24px" }}
                    onClick={() => setShowDropdown(!showDropdown)}
                  ></i>
                </div>
                <ul className="list-group">
                  <li>
                    <Link
                      to="/profile"
                      onClick={() => setShowDropdown(false)}
                      className="list-group-item"
                    >
                      <i className="fas fa-user-circle me-2" />
                      Your Profile
                    </Link>
                  </li>
                  {/* <li>
                    <Link
                      to="/wallet"
                      onClick={() => setShowDropdown(false)}
                      className="list-group-item"
                    >
                      {" "}
                      <i className="fas fa-user-circle me-2" /> Your Wallet
                    </Link>
                  </li> */}
                  <li>
                    <Link
                      to="/contact-us"
                      onClick={() => setShowDropdown(false)}
                      className="list-group-item"
                    >
                      {" "}
                      <i className="fa-solid fa-headset me-2" /> Contact Us
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/my-order"
                      onClick={() => setShowDropdown(false)}
                      className="list-group-item"
                    >
                      {" "}
                      <i className="fas fa-shopping-basket me-2" /> Your Orders
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/favourite"
                      onClick={() => setShowDropdown(false)}
                      className="list-group-item"
                    >
                      {" "}
                      <i className="fas fa-heart me-2" /> Favourites
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/about-us"
                      onClick={() => setShowDropdown(false)}
                      className="list-group-item"
                    >
                      {" "}
                      <i className="fas fa-address-card me-2" /> About Us
                    </Link>
                  </li>
                  <li>
                    <Link
                      to=""
                      className="list-group-item"
                      onClick={() => {
                        handleLogout();
                        setShowDropdown(false);
                      }}
                    >
                      <i className="fas fa-sign-out me-2" />
                      Logout
                    </Link>
                  </li>
                </ul>
              </div>
            )}

            <div
              className="collapse navbar-collapse"
              id="navbarSupportedContent"
            >
              <ul className="navbar-nav ms-auto mb-2 mb-lg-0">
                <li className="nav-item">
                  <Link className="nav-link" to="/">
                    Home
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    className="nav-link"
                    to=""
                    data-bs-toggle="offcanvas"
                    data-bs-target="#search-canvas"
                    aria-controls="offcanvasResponsive"
                  >
                    <i className="fa-solid fa-search"></i>
                  </Link>
                </li>
                {/* <li className="nav-item"> */}
                  {/* <Link className="nav-link cart-btn" to="/cart"><i className="fas fa-shopping-cart"></i>{" "}<span>{totalQuantity || 0}</span></Link> */}
                  {/* <a
                    className="nav-link cart-btn"
                    href={DEFAULT_APP_DOWNLOAD_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="fas fa-shopping-cart"></i>{" "}
                    <span>App</span>
                  </a> */}
                {/* </li> */}
                {!isLogin && (
                  <>
                    {" "}
                    <li className="nav-item">
                      <Link
                        className="nav-link"
                        to=""
                        data-bs-toggle="modal"
                        data-bs-target="#login-modal"
                        id="loginbutton"
                      >
                        Login
                      </Link>
                    </li>
                  </>
                )}
              </ul>
              {/* <li className="nav-item">
                <Link className="nav-link cart-btn" to="/notification">
                  <i className="fa-solid fa-bell"></i>
                </Link>
              </li> */}
              {isLogin && (
                <div className="dropdown profile-menu">
                  <button
                    className="dropdown-toggle"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    {isLogin && (
                      <img
                        src={user?.profile ? user?.profile : avtar}
                        alt="profile"
                      />
                    )}
                    <span>{user?.name ? user?.name : "Profile"}</span>
                  </button>
                  <ul className="dropdown-menu">
                    <li>
                      <Link className="dropdown-item" to="/profile">
                        <i className="fas fa-user-circle me-2" />
                        Your Profile
                      </Link>
                    </li>
                    {/* <li>
                      <Link className="dropdown-item" to="/wallet">
                        <i className="fas fa-user-circle me-2" />
                        Your wallet
                      </Link>
                    </li> */}
                    <li>
                      <Link className="dropdown-item" to="/contact-us">
                        <i className="fa-solid fa-headset me-2" />
                        Contact Us
                      </Link>
                    </li>
                    <li>
                      {/* <Link className="dropdown-item" to="/my-order"><i className="fas fa-shopping-basket me-2" />Your Orders</Link> */}
                      <a
                        className="dropdown-item"
                        href={DEFAULT_APP_DOWNLOAD_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="fas fa-shopping-basket me-2" />
                        Your Orders
                      </a>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/favourite">
                        <i className="fas fa-heart me-2" />
                        Favourites
                      </Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/about-us">
                        <i className="fas fa-address-card me-2" />
                        About us
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="dropdown-item"
                        to=""
                        onClick={handleLogout}
                      >
                        <i className="fas fa-sign-out me-2" />
                        Logout
                      </Link>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </nav>
        <SearchBar />
      </header>
    </>
  );
}

export default Header;
